import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2, AlertTriangle } from 'lucide-react';

import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import IconButton from '../../components/ui/IconButton';
import Tooltip from '../../components/ui/Tooltip';
import { getAtsSettings } from '../../services/atsSettingsService';
import {
  listRecruitingCosts, createRecruitingCost, updateRecruitingCost, deleteRecruitingCost,
} from '../../services/atsRecruitingCostService';
import { formatDate } from '../../utils/Utils';
import {
  RECRUITING_COST_CATEGORIES, DEFAULT_RECRUITING_COST_CURRENCY, formatMoney, summarizeCosts, EMPTY,
} from './recruitingCost';

// SPEC F3 — kadro talebinin İşe Alım Maliyetleri bölümü (detay çekmecesinin içinde).
//
// Yetki: okuma AT.Write VEYA kadronun HM'i (çağıran `canRead` ile süzer —
// yetkisiz kullanıcıya GET atılmaz, aksi hâlde interceptor 403 tostu basardı).
// Yazma YALNIZ AT.Write (`canWrite`): HM listeyi ve toplamı görür, ekle /
// düzenle / sil aksiyonları ona HİÇ çizilmez.
//
// Para birimi formda SEÇİLEMEZ: sunucu kaydı ATS ayarlarındaki
// `recruitingCostCurrency` ile yazar (döviz çevrimi yok → karışık toplam yok).

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 22px 0 10px;
`;

const HeadTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 10px;
`;

const SummaryTile = styled.div`
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
  padding: 10px 12px;
`;

const TileLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const TileValue = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 2px;
`;

const Warn = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: #8A5A00;
  background: #FFF7E6;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 10px;
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  padding: 10px 12px;
`;

const ItemMain = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const ItemMeta = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 2px;
  word-break: break-word;
`;

const ItemActions = styled.div`
  display: flex;
  gap: 6px;
`;

const Empty = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// Yerel gün (UTC değil): gece yarısından sonra toISOString dünü döndürürdü.
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function RecruitingCostsSection({
  requisitionId, canRead, canWrite, hiredCount,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [payload, setPayload] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [settingsCurrency, setSettingsCurrency] = useState(null);
  const [form, setForm] = useState({ open: false, row: null });
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    if (!canRead || !requisitionId) return undefined;
    let cancelled = false;
    Promise.all([listRecruitingCosts(requisitionId), getAtsSettings()])
      .then(([cRes, sRes]) => {
        if (cancelled) return;
        // api.js GET'lerde reject ETMEZ — gerçek başarı sinyali res.data.status.
        if (!cRes?.data?.status) { setLoadFailed(true); setPayload(null); } else {
          setLoadFailed(false);
          setPayload(cRes.data.data ?? []);
        }
        if (sRes?.data?.status) setSettingsCurrency(sRes.data.data?.recruitingCostCurrency || null);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [requisitionId, canRead, reloadKey]);

  const summary = useMemo(
    () => summarizeCosts(payload || [], {
      currency: settingsCurrency || DEFAULT_RECRUITING_COST_CURRENCY,
      hiredCount,
    }),
    [payload, settingsCurrency, hiredCount],
  );
  // Formda kullanılacak para birimi: AYAR (yanıttaki değer ayarı yansıtır).
  const currency = settingsCurrency || summary.currency;

  const categoryOptions = useMemo(() => RECRUITING_COST_CATEGORIES.map((c) => ({
    id: c, name: t(`ats.recruitingCost.category.${c}`),
  })), [t]);

  const schema = useMemo(() => [
    {
      name: 'currencyNotice',
      type: 'notice',
      content: t('ats.recruitingCost.currencyNotice', { currency }),
    },
    {
      name: 'category', label: t('ats.recruitingCost.categoryLabel'),
      type: 'select', options: categoryOptions, required: true, grid: 6,
    },
    {
      name: 'amount', label: t('ats.recruitingCost.amount', { currency }),
      type: 'number', min: 0.01, step: 0.01, required: true, grid: 6,
    },
    {
      name: 'incurredOn', label: t('ats.recruitingCost.incurredOn'),
      type: 'date', required: true, grid: 6,
    },
    { name: 'vendor', label: t('ats.recruitingCost.vendor'), type: 'text', grid: 6 },
    { name: 'description', label: t('ats.recruitingCost.description'), type: 'textarea', rows: 3, grid: 12 },
  ], [t, categoryOptions, currency]);

  if (!canRead) return null;

  const handleSave = async (values) => {
    const amount = Number(values.amount);
    if (!Number.isFinite(amount) || amount <= 0) return false;
    const body = {
      category: values.category,
      amount,
      // Para birimi GÖNDERİLMEZ: sunucu kaydı ayardaki para birimiyle yazar
      // (formda seçilemez; istemcinin bildiği değer bayat olabilir).
      incurredOn: values.incurredOn ? String(values.incurredOn).slice(0, 10) : null,
      vendor: values.vendor?.trim() || null,
      description: values.description?.trim() || null,
    };
    try {
      if (form.row?.id) await updateRecruitingCost(requisitionId, form.row.id, body);
      else await createRecruitingCost(requisitionId, body);
      setForm({ open: false, row: null });
      reload();
      return true;
    } catch {
      return false;
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      await deleteRecruitingCost(requisitionId, confirm.id);
      setConfirm(null);
      reload();
    } catch { /* interceptor */ }
    setDeleting(false);
  };

  const { items } = summary;

  return (
    <div data-role="ats-recruiting-costs">
      <Head>
        <HeadTitle>{t('ats.recruitingCost.sectionTitle')}</HeadTitle>
        {canWrite && (
          <Tooltip text={t('ats.recruitingCost.add')}>
            <IconButton
              type="button"
              size="sm"
              variant="gradient"
              aria-label={t('ats.recruitingCost.add')}
              data-role="ats-cost-add"
              onClick={() => setForm({ open: true, row: null })}
            >
              <Plus size={16} />
            </IconButton>
          </Tooltip>
        )}
      </Head>

      <Summary>
        <SummaryTile>
          <TileLabel>{t('ats.recruitingCost.total')}</TileLabel>
          <TileValue data-role="ats-cost-total">
            {loadFailed ? EMPTY : formatMoney(summary.totalCost, summary.currency, lang)}
          </TileValue>
        </SummaryTile>
        <SummaryTile>
          <TileLabel>{t('ats.recruitingCost.costPerHire')}</TileLabel>
          <TileValue data-role="ats-cost-per-hire">
            {loadFailed ? EMPTY : formatMoney(summary.costPerHire, summary.currency, lang)}
          </TileValue>
        </SummaryTile>
      </Summary>

      {summary.otherCurrencyCount > 0 && (
        <Warn data-role="ats-cost-other-currency">
          <AlertTriangle size={14} />
          <span>
            {t('ats.recruitingCost.otherCurrencyWarning', {
              count: summary.otherCurrencyCount, currency: summary.currency,
            })}
          </span>
        </Warn>
      )}

      {loadFailed && <Empty>{t('ats.recruitingCost.loadFailed')}</Empty>}
      {!loadFailed && items.length === 0 && <Empty>{t('ats.recruitingCost.empty')}</Empty>}
      {!loadFailed && items.length > 0 && (
        <List>
          {items.map((c) => (
            <Item key={c.id} data-role="ats-cost-row">
              <ItemMain>
                <ItemTop>
                  <span>{t(`ats.recruitingCost.category.${c.category}`, { defaultValue: c.category })}</span>
                  <span>{formatMoney(c.amount, c.currency || summary.currency, lang)}</span>
                </ItemTop>
                <ItemMeta>
                  {[c.incurredOn ? formatDate(c.incurredOn, lang) : null, c.vendor].filter(Boolean).join(' · ') || EMPTY}
                </ItemMeta>
                {c.description && <ItemMeta>{c.description}</ItemMeta>}
              </ItemMain>
              {canWrite && (
                <ItemActions>
                  <Tooltip text={t('common.edit')}>
                    <IconButton
                      type="button"
                      size="sm"
                      aria-label={t('common.edit')}
                      data-role="ats-cost-edit"
                      onClick={() => setForm({ open: true, row: c })}
                    >
                      <Pencil size={14} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip text={t('common.delete')}>
                    <IconButton
                      type="button"
                      size="sm"
                      variant="danger"
                      aria-label={t('common.delete')}
                      data-role="ats-cost-delete"
                      onClick={() => setConfirm(c)}
                    >
                      <Trash2 size={14} />
                    </IconButton>
                  </Tooltip>
                </ItemActions>
              )}
            </Item>
          ))}
        </List>
      )}

      {canWrite && (
        <>
          <GenericFormDrawer
            open={form.open}
            onClose={() => setForm({ open: false, row: null })}
            title={form.row ? t('ats.recruitingCost.edit') : t('ats.recruitingCost.add')}
            schema={schema}
            initialValues={form.row
              ? {
                category: form.row.category,
                amount: form.row.amount,
                incurredOn: form.row.incurredOn ? String(form.row.incurredOn).slice(0, 10) : '',
                vendor: form.row.vendor || '',
                description: form.row.description || '',
              }
              : { category: null, amount: '', incurredOn: todayIso(), vendor: '', description: '' }}
            onSave={handleSave}
          />
          <ConfirmDialog
            isOpen={!!confirm}
            title={t('common.delete')}
            message={t('ats.recruitingCost.deleteConfirm')}
            onConfirm={handleDelete}
            onCancel={() => setConfirm(null)}
            loading={deleting}
          />
        </>
      )}
    </div>
  );
}
